import {
  hasUnlimitedSchedulersOnly,
  PriceRecurringType,
} from "@practice/common";
import {
  OnTrackStatus,
  PackageInstanceCard,
  PackageInstanceCardCyclesInner,
  PackageInstancePublic,
  PackageInstancePublicCyclesInner,
  Tier,
} from "@practice/sdk";
import { PackageSchedulerType } from "api-services/definitions/package-instances";
import { capitalize, compact, isEmpty, isInteger, isNil, sortBy } from "lodash";
import moment from "moment";

import { displayNameFromContact } from "@lib/contacts";
import { AccountType } from "@lib/data/schemas/account";
import {
  ExtendedPackageType,
  PackageFormDataType,
  PackageFrequencyTypeType,
  PackageTimeType,
  PackageType,
  PriceFrequencyType,
} from "@lib/data/schemas/packages";
import { SchedulerType } from "@lib/data/schemas/scheduler";
import { TaxTypeType } from "@lib/data/schemas/taxType";
import { getCurrentCycle } from "@lib/models/package-instances/utils";
import { findAutoChargeWeekdayValue } from "@lib/models/packages/utils/transform-input-form";
import { displayItemAmountString } from "@lib/products";
import { convertHoursToMinutes, getOrdinal } from "@lib/utils";
import pluralHelper from "@lib/utils/pluralHelper";

/**
 * Return the total number of appointments in a package or package instance
 * based on the items or the totalSessions field
 * */
type FrequencyCopiesValuesType = "abbreviation" | "singular" | "intervals";
type FrequencyCopiesType = Record<
  PackageFrequencyTypeType,
  Record<FrequencyCopiesValuesType, string>
>;
export const frequencyCopies: FrequencyCopiesType = {
  weeks: {
    abbreviation: "wk",
    singular: "week",
    intervals: "weekly",
  },
  months: {
    abbreviation: "mo",
    singular: "month",
    intervals: "monthly",
  },
  years: {
    abbreviation: "yr",
    singular: "year",
    intervals: "yearly",
  },
};

export const getTotalAvailable = (
  data: PackageInstanceCard | PackageInstancePublic | PackageType,
  currentCycleOnly?: boolean,
  fromCycle?:
    | PackageInstanceCardCyclesInner
    | PackageInstancePublicCyclesInner
    | null
) => {
  if ("packageId" in data) {
    const cycles = data?.cycles ? data.cycles : [];
    const fromCycleIndex = cycles.findIndex((cycle) => cycle === fromCycle);
    if (fromCycleIndex !== -1) {
      const currentCycle = cycles[fromCycleIndex];
      return currentCycle?.totalAvailable ?? data?.totalAvailable ?? 0;
    }

    if (currentCycleOnly) {
      const currentCycle = getCurrentCycle(data);
      return currentCycle?.totalAvailable ?? data?.totalAvailable ?? 0;
    }

    return data?.totalAvailable ?? 0;
  } else {
    return data?.frequency?.total ?? 0;
  }
};

const getFrequencyType = (
  data: PackageInstanceCard | PackageInstancePublic | PackageType
) => {
  if ("packageId" in data) {
    return data?.frequency ?? "months";
  }
  return data?.frequency?.type ?? "months";
};

const getLowestTierFromFrequency = (data: Tier[]) => {
  if (data.length === 1) return data[0];
  return data?.reduce((acc, item) => {
    return item.content.quantity <= (acc?.content?.quantity ?? 0) ? item : acc;
  }, data?.[0]);
};

export const countPackageAppointments = (
  data: PackageInstanceCard | PackageInstancePublic | ExtendedPackageType,
  frequencyCopiesType: FrequencyCopiesValuesType | null = "abbreviation",
  label: string | undefined = undefined,
  currentCycleOnly?: boolean,
  fromCycle?:
    | PackageInstanceCardCyclesInner
    | PackageInstancePublicCyclesInner
    | null
) => {
  const isPackageInstance = "packageId" in data;
  const hasTiers = "tiers" in data;
  if (data.packageType === "flexible") {
    if (isPackageInstance) return data.totalAvailable;
    return "Flexible";
  }
  const isContentTypeTime = data?.contentType === "time";
  const isReccuringBased = data?.packageType === "recurring";
  const isSubscriptionBased = data?.packageType === "subscription";
  const timeType = data?.timeType || "hours";
  const timeTypeSingular = timeType.substring(0, timeType.length - 1);

  if (isReccuringBased || isSubscriptionBased) {
    const isSingleTier = hasTiers && data?.tiers?.length === 1;
    const tier = hasTiers
      ? getLowestTierFromFrequency(data?.tiers ?? [])
      : null;
    const packageData = tier
      ? ({
          frequency: {
            type: tier?.content.period ?? "months",
            total: tier?.content.quantity,
          },
        } as ExtendedPackageType)
      : data;
    const frequency = getFrequencyType(packageData);
    const frequencyCopy = frequencyCopies[frequency];
    const isFrequencyCopiesTypeNull = frequencyCopiesType === null;

    const frequencyCopyValue = isFrequencyCopiesTypeNull
      ? ""
      : frequencyCopy[frequencyCopiesType];

    const frequencyTotal = getTotalAvailable(
      packageData,
      currentCycleOnly,
      fromCycle
    )!;

    const isFrequencyCopyIntervals = frequencyCopiesType === "intervals";
    const totalType = isContentTypeTime ? timeTypeSingular : "appt";
    const value = pluralHelper(frequencyTotal, totalType);

    if (isFrequencyCopiesTypeNull) {
      return frequencyTotal;
    }

    const prefix = hasTiers && !isSingleTier ? "Starting at " : "";

    return isFrequencyCopyIntervals
      ? `${frequencyTotal} ${frequencyCopyValue}`
      : `${prefix}${value}/${frequencyCopyValue}`;
  }

  if (isContentTypeTime) {
    if (isPackageInstance)
      return pluralHelper(data?.totalAvailable ?? 0, timeTypeSingular);

    return pluralHelper(data?.totalSessions ?? 0, timeTypeSingular);
  }

  if (data?.distributeSessions) {
    if (!isPackageInstance) return data?.totalSessions ?? 0;
    return data.totalAvailable;
  }

  const quantityItems = data?.items?.map((item: any) => item.quantity) || [];

  const total = quantityItems?.reduce<number>((acc, quantity) => {
    return acc + quantity;
  }, 0);

  if (label) {
    return pluralHelper(total || 0, label);
  } else {
    return total;
  }
};

export const getUniquePackageAccountNames = (
  accounts: Partial<AccountType>[]
) => {
  const names = accounts.map((a: any) => displayNameFromContact(a, true));
  const uniqueNames = [...new Set(names)];
  return uniqueNames.join(", ");
};

export const getRemainingAppointments = (
  scheduler: PackageSchedulerType
): number => {
  const quantity = scheduler?.quantity || 0;
  const completed = scheduler?.appointments?.length || 0;
  const remaining = quantity - completed;
  return Math.max(0, remaining);
};

export const filterDuplicateSchedulersByPackageInstanceId = (
  packageInstanceId: string,
  packageSchedulers: PackageSchedulerType[]
) => {
  const groupedSchedulers: {
    [key: string]: PackageSchedulerType[];
  } = {};
  packageSchedulers?.forEach((scheduler) => {
    if (groupedSchedulers[scheduler.id]) {
      groupedSchedulers[scheduler.id].push(scheduler);
    } else {
      groupedSchedulers[scheduler.id] = [scheduler];
    }
  });

  const filtered = Object.keys(groupedSchedulers).map((key) => {
    const schedulers = groupedSchedulers[key];
    if (schedulers.length === 1) {
      return schedulers[0];
    } else {
      const scheduler = schedulers.find(
        (s) =>
          s.packageInstanceId === packageInstanceId &&
          getRemainingAppointments(s)
      );
      return scheduler || schedulers[0];
    }
  });

  return filtered;
};

/**
 * Returns the total seconds of all schedulers
 * */
export const getAllSchedulersInMinutes = (
  schedulers: PackageSchedulerType[] = []
) => schedulers?.reduce((acc, item) => acc + (item?.duration || 0), 0);

/**
 * Returns true if the total duration of all schedulers is greater than the
 * total value
 * */
export const isDurationGreaterThenTotal = (
  schedulers: SchedulerType[],
  total: number,
  timeType: PackageTimeType
) => {
  const value = total ?? 0;
  const type = timeType ?? "hours";
  const totalSchedulersInMinutes = getAllSchedulersInMinutes(schedulers);
  const totalSessionsInMinutes =
    type === "hours" ? convertHoursToMinutes(value) : value;

  return totalSchedulersInMinutes > totalSessionsInMinutes;
};

/**
 * Formats a substitle given a package
 * */
export const getPackageSubtitle = (
  packageItem: ExtendedPackageType | PackageInstanceCard
) => {
  if (packageItem.items?.length === 1 && packageItem.items[0].curriculum) {
    return "Curriculum";
  }
  const packageType = packageItem?.packageType ?? "";
  const isSubscriptionBased = packageType === "subscription";
  const isUnlimitedOnly = hasUnlimitedSchedulersOnly(packageItem);
  if (packageType === "usage") {
    const suggestion = getSuggestedUsagePackageInfo(packageItem);
    return suggestion?.suggestedSubtitle ?? "";
  }

  const apptInfo = isUnlimitedOnly
    ? "Unlimited appointments"
    : compact([
        countPackageAppointments(packageItem),
        packageItem?.contentType === "time" ||
        ["recurring", "flexible", "subscription"].includes(packageType)
          ? ""
          : "appointments",
      ]).join(" ");

  if ("tiers" in packageItem && isSubscriptionBased) {
    const totalTiers = packageItem?.tiers?.length ?? 0;
    if (totalTiers > 1) {
      return pluralHelper(totalTiers, "tier");
    } else {
      return apptInfo;
    }
  }

  return apptInfo;
};

export const getPackageTemplatePriceTitle = (
  packageTemplate: ExtendedPackageType,
  totalInCents: number = 0
) => {
  if (
    "tiers" in packageTemplate &&
    packageTemplate?.packageType === "subscription"
  ) {
    const tiers = packageTemplate?.tiers || [];
    const totalTiers = tiers.length;
    if (totalTiers === 1) {
      return tiers[0]?.startingPriceTitle || "";
    } else {
      return pluralHelper(totalTiers, "different price option");
    }
  }

  const isFree = isEmpty(packageTemplate.paymentOptions);
  const currencyString = isFree
    ? undefined
    : packageTemplate.paymentOptions.length === 1
      ? displayItemAmountString(
          totalInCents / 100,
          packageTemplate.paymentOptions[0].currency
        )
      : "Multiple";
  return currencyString;
};

export const getFlexiblePackageUnit = (packageItem: PackageType) => {
  if (packageItem.packageType !== "flexible") return "";
  return packageItem?.contentType === "sessions" ? "session" : "hour";
};

export const getPackageInstanceUnit = (
  packageInstance: PackageInstanceCard,
  recurring: boolean
) => {
  const unit =
    packageInstance.contentType === "sessions"
      ? "sessions"
      : packageInstance.timeType;

  if (packageInstance.frequency) {
    if (!recurring) return unit;
    return `${unit} per ${packageInstance.frequency.slice(0, -1)}`;
  }
  return unit;
};

export const cheapestSuggestedOption = (items: PackageType["items"]) =>
  items.length > 1 &&
  items.reduce((prev, curr) =>
    (prev.usagePricing?.amount ?? 0) < (curr.usagePricing?.amount ?? 0)
      ? prev
      : curr
  );

export const mostExpensiveSuggestedOption = (items: PackageType["items"]) =>
  items.length > 1 &&
  items.reduce((prev, curr) =>
    (prev.usagePricing?.amount ?? 0) > (curr.usagePricing?.amount ?? 0)
      ? prev
      : curr
  );

export const getSuggestedUsagePackageInfo = (
  packageObject: PackageType | PackageInstanceCard
) => {
  const hasAtLeastOneMonthly = packageObject?.items?.some(
    (item: any) => item.suggestedFrequency?.type === "months"
  );
  const suggestedAmountTotal = packageObject?.items?.reduce((acc, item) => {
    const isWeekly = item.suggestedFrequency?.type === "weeks";
    const suggestedTotal = item.suggestedFrequency?.total ?? 0;
    return (
      acc +
      (isWeekly && hasAtLeastOneMonthly ? suggestedTotal * 4 : suggestedTotal)
    );
  }, 0);
  if (suggestedAmountTotal <= 0) return null;
  const isSessions = packageObject.contentType === "sessions";
  const suggestedTimeFrame = hasAtLeastOneMonthly ? "monthly" : "weekly";
  const shortenedTimeFrame = hasAtLeastOneMonthly ? "mo" : "wk";
  const suggestionBase = `${suggestedAmountTotal} ${
    isSessions ? "appts" : "hours"
  }`;
  const suggestedSubtitle = suggestedAmountTotal
    ? `${suggestionBase}/${shortenedTimeFrame}`
    : "";
  const suggestedInfo = `${suggestionBase} suggested ${suggestedTimeFrame}`;

  return {
    suggestedAmountTotal,
    suggestedTimeFrame,
    suggestedSubtitle,
    suggestedInfo,
  };
};

export const getPackageSchedulerSuggestionInfo = (
  scheduler: PackageType["items"][0] & {
    contentType: PackageType["contentType"];
  }
) => {
  if (!scheduler.suggestedFrequency || !scheduler.contentType)
    return { suggestedSubtitle: "" };
  const suggestedFrequency = scheduler.suggestedFrequency;
  const suggestedAmount = suggestedFrequency?.total;
  const suggestedType = suggestedFrequency?.type;
  const isSessions = scheduler.contentType === "sessions";
  const suggestedTimeFrame = suggestedType === "months" ? "mo" : "wk";
  const suggestedSubtitle = suggestedAmount
    ? `${suggestedAmount} ${
        isSessions ? "appts" : "hrs"
      } suggested/${suggestedTimeFrame}`
    : "";

  return {
    suggestedAmount,
    suggestedTimeFrame,
    suggestedSubtitle,
  };
};

const getUsageInvoiceSubtitle = (
  usageInvoicing?: PackageType["usageInvoicing"]
) => {
  if (!usageInvoicing) return;
  const { dueAfterDays, method, period, autoChargeDay, cutOffDate } =
    usageInvoicing;
  const isAuto = method === "auto";
  if (isAuto) {
    return period === "months"
      ? `Charged automatically on the ${getOrdinal(
          autoChargeDay ?? 1
        )} of the month`
      : `Charged automatically on ${capitalize(
          findAutoChargeWeekdayValue(cutOffDate!, autoChargeDay!)
        )}`;
  } else if (dueAfterDays) {
    return `Pay manually on the ${getOrdinal(dueAfterDays)} of the month`;
  }
};

export const getUsagePackageInfo = (packageObject: PackageType) => {
  if (packageObject.packageType !== "usage") return null;
  const options = packageObject.paymentOptions;
  const items = packageObject.items;
  const deposit = options[0];
  const { period } = packageObject.usageInvoicing || {};
  const isMonthlyInvoicing = period === "months";
  const usageInvoiceTitle =
    deposit && `Receive ${isMonthlyInvoicing ? "monthly" : "weekly"} invoices`;

  const usageInvoiceSubtitle = getUsageInvoiceSubtitle(
    packageObject.usageInvoicing
  );

  const usageDepositTitle = deposit
    ? `${displayItemAmountString(
        deposit.splits?.reduce((acc, item) => acc + item.total, 0) ??
          deposit.amount,
        deposit.currency
      )} due at booking`
    : "Deposit not required";
  const usageDepositSubtitle = deposit ? "Taxes included" : "";

  const suggestion = getSuggestedUsagePackageInfo(packageObject);
  const usageSuggestedSubtitle = "With various durations";

  const usagePaymentTitle = "Pay as you go";
  const leastExpensiveOption = cheapestSuggestedOption(items);
  const expensiveOption = mostExpensiveSuggestedOption(items);

  const cheapestAmount =
    leastExpensiveOption && leastExpensiveOption.usagePricing?.amount;
  const cheapestCurrency =
    leastExpensiveOption && leastExpensiveOption.usagePricing?.currency;
  const expensiveAmount =
    expensiveOption && expensiveOption.usagePricing?.amount;
  const expensiveCurrency =
    expensiveOption && expensiveOption.usagePricing?.currency;
  const areSamePrice = cheapestAmount === expensiveAmount;

  const formattedLeastExpensive = displayItemAmountString(
    cheapestAmount,
    cheapestCurrency
  );
  const formattedExpensive =
    expensiveOption &&
    displayItemAmountString(expensiveAmount, expensiveCurrency);

  const usagePaymentSubtitle = areSamePrice
    ? formattedLeastExpensive
    : `Price range: ${formattedLeastExpensive} - ${formattedExpensive}`;

  return {
    usageInvoiceTitle,
    usageInvoiceSubtitle,
    usageDepositTitle,
    usageDepositSubtitle,
    usageSuggestedTitle: suggestion?.suggestedInfo,
    usageSuggestedSubtitle,
    usagePaymentTitle,
    usagePaymentSubtitle,
  };
};

export const sortedTaxTypeOptions = (taxTypes: TaxTypeType[]) => {
  return sortBy(taxTypes, [(t) => t.name]).map((taxType) => ({
    value: taxType.id,
    label: `${taxType.name} (${taxType.percentage}%, ${
      taxType.inclusive ? "inclusive" : "exclusive"
    })`,
  }));
};

export const formatOnTrackPayload = (payload, isPackageInstance = false) => {
  const { onTrackRules, onTrackNotifications, onTrackEnabled, enableAtRisk } =
    payload ?? {};

  const {
    offTrackSessionCount,
    onTrackDaysInterval,
    allowClientView,
    atRiskSessionCount,
  } = onTrackRules ?? {};

  if (!onTrackEnabled)
    return {
      onTrackRules: null,
      onTrackNotifications: null,
      ...(isPackageInstance && { onTrackEnabled: false }),
    };

  const riskSessions = enableAtRisk ? Number(atRiskSessionCount) : null;

  const formattedRules = {
    atRiskSessionCount: riskSessions,
    offTrackSessionCount: Number(offTrackSessionCount),
    onTrackDaysInterval: Number(onTrackDaysInterval),
    allowClientView: allowClientView ?? false,
  };

  const { atRisk, offTrack } = onTrackNotifications ?? {};
  const transformToNumber = (notif) =>
    notif?.map((n) => ({
      ...n,
      afterDays: Number(n.afterDays),
    }));

  const riskNotifs = enableAtRisk ? transformToNumber(atRisk) : null;

  const formattedNotifs =
    atRisk?.length || offTrack?.length
      ? {
          atRisk: riskNotifs,
          offTrack: transformToNumber(offTrack),
        }
      : null;

  return {
    onTrackRules: formattedRules,
    onTrackNotifications: formattedNotifs,
    ...(isPackageInstance && {
      onTrackEnabled: true,
      ...(onTrackEnabled && { onTrackStatus: OnTrackStatus.OnTrack }),
    }),
  };
};

/**
 * This function is used to validate the frequency field in the package template
 * and tier forms
 * */
export const validateFrequency = (
  value: PackageFormDataType["frequency"],
  isTimeBased = false,
  isDurationLowerThanTotalSessions = false
): string | true => {
  if (!value || isNil(value.total)) return "Frequency is required";
  if (isTimeBased && !isDurationLowerThanTotalSessions) {
    return "Frequency is less than total schedulers available";
  }
  if (!isTimeBased) {
    if (value.total < 0) return "Frequency must be positive";
    if (!isInteger(value.total)) return "Frequency must be a whole number";
  }
  return true;
};

export const getPriceFrequencyToForm = (priceFrequency: {
  intervalCount: number;
  interval: PriceFrequencyType;
}) => {
  // should get the priceFrequency, and return the approriate PriceFrequencyType related
  if (priceFrequency.interval === "month") {
    switch (priceFrequency.intervalCount) {
      case 1:
        return "month";
      case 3:
        return "quarter";
      case 6:
        return "biannual";
      case 12:
        return "year";
      default:
        return "month";
    }
  }
  if (priceFrequency.interval === "week") {
    return "week";
  }

  if (priceFrequency.interval === "year") {
    return "year";
  }
  return "month";
};

export const getPriceRecurringValueToApi = (
  priceFrequency: string
): PriceRecurringType => {
  const defaultFrequency = {
    intervalCount: 1,
    interval: "month",
  } as PriceRecurringType;

  switch (priceFrequency) {
    case "month":
      return defaultFrequency;
    case "week":
      return {
        intervalCount: 1,
        interval: "week",
      };
    case "quarter":
      return {
        intervalCount: 3,
        interval: "month",
      };
    case "biannual":
      return {
        intervalCount: 6,
        interval: "month",
      };
    case "year":
      return {
        intervalCount: 1,
        interval: "year",
      };
    default:
      return defaultFrequency;
  }
};

/**
 * This function was created to get the original date from a date object saved at the start of day UTC that needs
 * to be displayed on calendar form field with exactly the same date.
 * Today the component does not support timezones, so we need to get the original date from the UTC date, otherwise
 * the date will be displayed with the timezone offset.
 */
export const getUTCOriginalDate = (date: Date | null) => {
  if (!date) return null;
  const utcDateOnly = moment(date).utc().format("YYYY-MM-DD");
  return moment(utcDateOnly).toDate();
};

/**
 * Get the initial tier for a package. The function looks for the popular tier, if there is no popular tier, it will
 * return the first tier. If there are 3 or more tiers and no popular tier, it will return the second tier.
 */
export const getInitialTier = (packageObject: ExtendedPackageType) => {
  const tiers = packageObject?.tiers ?? [];
  if (!tiers.length) {
    return {
      initialTier: null,
      initialTierPriceId: null,
    };
  }

  const popularTier = tiers.find((tier) => tier.isPopular);
  const totalTiers = tiers.length ?? 0;
  const MIN_TIERS_FOR_MIDDLE_SELECTION = 3;
  const fallbackTierIndex = totalTiers < MIN_TIERS_FOR_MIDDLE_SELECTION ? 0 : 1;
  const tierIndex = popularTier
    ? tiers.indexOf(popularTier)
    : fallbackTierIndex;
  const initialTier = tiers?.[tierIndex] ?? null;
  const initialTierPriceId = initialTier?.prices?.[0]?.id ?? null;
  return {
    initialTier,
    initialTierPriceId,
  };
};
